import React from 'react';
import { Alert, Button, Empty } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

export interface DownloadDeliverableFallbackProps {
  /** Presigned S3 URL for the document bytes. */
  downloadUrl?: string;
  /** Original file name, shown alongside the download affordance. */
  fileName: string;
}

/**
 * Fallback for deliverable formats that have no inline viewer (`other` / unknown —
 * Decision 6): there is no preview, only a download, mirroring the panel's "Get"
 * (`window.open(downloadUrl)`). When no presigned URL is available, an inline error
 * is shown instead of a dead button.
 * @param {DownloadDeliverableFallbackProps} props - The presigned URL and file name.
 * @returns {React.ReactElement} A download affordance, or an inline error state.
 */
export function DownloadDeliverableFallback({
  downloadUrl,
  fileName
}: DownloadDeliverableFallbackProps): React.ReactElement {
  if (!downloadUrl) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <Alert
          type="error"
          showIcon
          message="Unable to download deliverable"
          description="No download URL is available for this deliverable."
        />
      </div>
    );
  }

  return (
    <div className="h-full flex items-center justify-center p-6">
      <Empty description={`This deliverable can't be previewed. Download ${fileName} to view it.`}>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => window.open(downloadUrl, '_blank', 'noopener,noreferrer')}
        >
          Download
        </Button>
      </Empty>
    </div>
  );
}
